import React, { useState } from 'react';
import { compose } from 'redux';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, InputAdornment } from '@mui/material';
import { Search, Sync } from '@mui/icons-material';
import ContractInfoComponent from './ContractInfoComponent';
import { shape, required } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation, isValid, getErrors } from '@totalsoft/pure-validations-react';
import { isEmpty } from 'lodash';
import { saveLeasingContract } from '../actionCreators';
import { Autocomplete, Button, IconButton, TextField } from '@totalsoft/rocket-ui';
import { useMemo } from 'react';

export const contractModificationValidator = shape({
  modificationTypeId: required,
  contractNumber: required
});

const ContractComponent = ({
  t,
  openContractSelection,
  leasingOfferContractData,
  isNewContractModification,
  contractSelected,
  modificationTypes,
  onModificationChange,
  offerTypeId,
  onChangeOpenReinitializationConfirmBool,
  reinitializationProcessing
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const [validation, validate] = useDirtyFieldValidation(contractModificationValidator);

  const handleSaveContractSelection = () => {
    if (!validate(contractSelected)) {
      return;
    }
    setIsSaving(true);
    dispatch(saveLeasingContract(contractSelected)).then(data => {
      setIsSaving(false);
      const { documentId, siteId } = data.eventBody;
      history.push(`/offers/${documentId}/${siteId}`);
    });
  };

  const handleReinitialization = () => {
    onChangeOpenReinitializationConfirmBool(true);
  };

  const modificationType = useMemo(
    () => (modificationTypes || []).find(type => type.offerTypeId === (contractSelected?.modificationTypeId || offerTypeId)),
    [contractSelected, modificationTypes, offerTypeId]
  );

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            id={'contractCard_modificationType'}
            fullWidth
            disabled={!isNewContractModification}
            value={modificationType || ''}
            onChange={onModificationChange}
            label={t('RestructuringEdit.Contract.ModificationType')}
            labelKey={'offerTypeName'}
            valueKey={'offerTypeId'}
            options={modificationTypes}
            error={!isValid(validation.modificationTypeId)}
            helperText={getErrors(validation.modificationTypeId)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id={'contractCard_contractNumber'}
            fullWidth
            disabled
            value={contractSelected?.contractNumber || leasingOfferContractData.contractNumber || ''}
            label={t('RestructuringEdit.Contract.Number')}
            readOnly
            endAdornment={
              isNewContractModification ? (
                <InputAdornment position='end'>
                  <IconButton
                    id={'contractCard_selectionBtn'}
                    tooltip={t('Tooltips.ContractSelection')}
                    color='primary'
                    variant='text'
                    size='tiny'
                    onClick={openContractSelection}
                    disabled={!isNewContractModification}
                  >
                    <Search />
                  </IconButton>
                </InputAdornment>
              ) : (
                leasingOfferContractData.isInitialState && (
                  <InputAdornment position='end'>
                    <IconButton
                      id={'contractCard_RecomputeBtn'}
                      tooltip={t('RestructuringEdit.Contract.RecomputeSimulations')}
                      color='primary'
                      variant='text'
                      size='tiny'
                      onClick={handleReinitialization}
                      disabled={isSaving || reinitializationProcessing}
                    >
                      <Sync />
                    </IconButton>
                  </InputAdornment>
                )
              )
            }
            error={!isValid(validation.contractNumber)}
            helperText={getErrors(validation.contractNumber)}
          />
        </Grid>
        <Grid item xs={12} container>
          {isNewContractModification && contractSelected && (
            <Button
              style={{ marginRight: '1rem' }}
              id={'contractSelection_saveBtn'}
              onClick={handleSaveContractSelection}
              color='primary'
              variant='contained'
              disabled={isEmpty(contractSelected) || isSaving}
            >
              {isSaving ? t('Saving') : t('Save')}
            </Button>
          )}
        </Grid>
      </Grid>
      {!isNewContractModification && leasingOfferContractData.contractNumber && (
        <ContractInfoComponent leasingOfferContractData={leasingOfferContractData} />
      )}
    </>
  );
};

ContractComponent.propTypes = {
  t: PropTypes.func.isRequired,
  onModificationChange: PropTypes.func,
  openContractSelection: PropTypes.func,
  modificationTypes: PropTypes.array,
  leasingOfferContractData: PropTypes.object.isRequired,
  isNewContractModification: PropTypes.bool.isRequired,
  contractSelected: PropTypes.object,
  offerTypeId: PropTypes.number,
  onChangeOpenReinitializationConfirmBool: PropTypes.func,
  reinitializationProcessing: PropTypes.bool
};

export default compose(withTranslation())(ContractComponent);
